import { CModal, CModalBody, CSpinner } from "@coreui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { Settings } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Link } from "react-router-dom";
import DeleteIcon from "../../components/icons/DeleteIcon";
import Pagination from "../../components/ui/Pagination";
import RenderTable from "../../components/ui/RenderTable";
import { colors } from "../../config/theme";
import { controlStyle } from "../../helpers/constants";
import { Chip, toQuery } from "../../helpers/general";
import { useCookie } from "../../hooks/useCookie";
import { useDebounce } from "../../hooks/useDebounce";
import { useToast } from "../../hooks/useToast";
import { general } from "../../locales/general";
import apiService from "../../service/apiService";
import { cardStyle } from "../Places/PlacesTable";
import AdminRoute from "../auth/RestrictedRoute";
import RewardList from "./rewardList";

const PAGE_SIZE = 10;

interface Rewards {
  meta: PaginationMeta;
  items: Reward[];
}

export default function RewardsPage() {
  const { token } = useCookie("vToken");
  const { toastError, toastSuccess } = useToast();
  const queryClient = useQueryClient();

  const [currentPage, setCurrentPage] = useState(1);
  const [query, setQuery] = useState("");
  const [type, setType] = useState("");
  const [isActive, setIsActive] = useState("");
  const [isReusable, setIsReusable] = useState("");
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [points, setPoints] = useState<string>("");
  const [configType, setConfigType] = useState("");
  const [selectedRewards, setSelectedRewards] = useState<string[]>([]);

  const searchQuery = useDebounce(query, 300);

  // Search or filter change goes back to page 1
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, type, isActive, isReusable]);

  const endpoint = `rewards${toQuery({
    page: currentPage,
    query: searchQuery,
    type,
    isActive,
    isReusable,
  })}`;

  const { data, isLoading, isPreviousData } = useQuery<Rewards>({
    queryKey: ["getRewards", endpoint],
    queryFn: () => apiService.MakeGetRequest(endpoint, token),
    keepPreviousData: true,
    enabled: Boolean(token),
  });

  const deleteRewardMutation = useMutation({
    mutationFn: (id: string) =>
      apiService.MakeDeleteRequest(`rewards/${id}`, token),
  });

  // Stable identity: rows call it from an unmount cleanup
  const handleRewardSelection = useCallback(
    (id: string, action: "add" | "remove") => {
      setSelectedRewards((prev) =>
        action === "add"
          ? prev.includes(id)
            ? prev
            : [...prev, id]
          : prev.filter((rewardId) => rewardId !== id),
      );
    },
    [],
  );

  const handleBulkDelete = async () => {
    const ids = [...selectedRewards];
    const results = await Promise.allSettled(
      ids.map((id) => deleteRewardMutation.mutateAsync(id)),
    );

    // Only the rewards that were really deleted leave the selection
    results.forEach((r, i) => {
      if (r.status === "fulfilled") handleRewardSelection(ids[i], "remove");
    });

    if (results.some((r) => r.status === "rejected")) {
      toastError(general.fr.message.operationFailed);
    } else {
      toastSuccess("Reward deleted successfully");
    }
    queryClient.invalidateQueries({ queryKey: ["getRewards"] });
  };

  const rewards = data?.items ?? [];
  const total = data?.meta?.totalItems ?? 0;
  const selectedCount = selectedRewards.length;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { data: configData } = useQuery({
    queryKey: ["rewardConfig"],
    queryFn: () => apiService.MakeGetRequest(`rewards/configs?'RIDE'`, token),
    enabled: showConfigModal,
    onSuccess: (data) => {
      setPoints(data?.basePoints || "");
      setConfigType(data?.type || "");
    },
  });

  const updatePointsMutation = useMutation({
    mutationFn: ({ points, type }: { points: number; type: string }) =>
      apiService.MakePutRequest(
        `rewards/configs`,
        { basePoints: Number(points), type },
        token,
      ),
    onSuccess: () => {
      toastSuccess("Points updated successfully");
      setShowConfigModal(false);
    },
    onError: () => toastError("Failed to update points"),
  });

  return (
    <AdminRoute>
      <section>
        {/* Title + count + actions */}
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <div className="d-flex align-items-center gap-2">
            <h5 className="fs-4 fw-bold mb-0">Bonus</h5>
            {data && <Chip tone="warning">{total} Bonus</Chip>}
          </div>

          <div className="d-flex flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-outline-primary rounded-pill px-3 d-flex align-items-center gap-2"
              onClick={() => setShowConfigModal(true)}
            >
              <Settings width="16" height="16" />
              Configurer les points
            </button>
            <Link
              to="/rewards/new"
              className="btn btn-primary text-white rounded-pill px-4"
            >
              + Ajouter un Bonus
            </Link>
          </div>
        </div>

        {/* Toolbar */}
        <div
          style={{ ...cardStyle, padding: "12px 16px" }}
          className="d-flex flex-wrap align-items-center gap-2 mb-3"
        >
          <div
            className="d-flex align-items-center gap-2 px-3 flex-grow-1"
            style={{ ...controlStyle, minWidth: 200, maxWidth: 360 }}
          >
            <MagnifyingGlassIcon color="gray" width={18} />
            <input
              type="text"
              placeholder="Rechercher un bonus"
              className="border-0 w-100 bg-transparent"
              style={{ outline: "none", fontSize: 13 }}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <select
            className="form-select"
            style={{ ...controlStyle, width: "auto" }}
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            <option value="">Tous les types</option>
            <option value="OFFER">Offre</option>
            <option value="PACK">Pack</option>
            <option value="EVENT">Événement</option>
            <option value="DISCOUNT">Promo</option>
          </select>

          <select
            className="form-select"
            style={{ ...controlStyle, width: "auto" }}
            value={isActive}
            onChange={(e) => setIsActive(e.target.value)}
          >
            <option value="">Tous les statuts</option>
            <option value="true">Actif</option>
            <option value="false">Inactif</option>
          </select>

          <select
            className="form-select"
            style={{ ...controlStyle, width: "auto" }}
            value={isReusable}
            onChange={(e) => setIsReusable(e.target.value)}
          >
            <option value="">Réutilisable : tous</option>
            <option value="true">Réutilisable</option>
            <option value="false">One-time</option>
          </select>

          <button
            type="button"
            disabled={deleteRewardMutation.isLoading || selectedCount === 0}
            onClick={handleBulkDelete}
            className="btn btn-outline-danger rounded-pill ms-auto d-flex align-items-center gap-2 px-3"
          >
            {deleteRewardMutation.isLoading ? (
              <>
                <CSpinner size="sm" /> {general.fr.wait}
              </>
            ) : (
              <>
                <DeleteIcon fill={colors.primary} /> Supprimer
                {selectedCount > 0 && ` (${selectedCount})`}
              </>
            )}
          </button>
        </div>

        {/* Table */}
        <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
          <RenderTable
            loading={isLoading || isPreviousData}
            render={() =>
              rewards.length > 0 ? (
                <RewardList
                  rewards={rewards}
                  onSelect={handleRewardSelection}
                />
              ) : (
                <p className="text-center text-muted py-5 mb-0">
                  {general.fr.noData}
                </p>
              )
            }
          />
        </div>

        <Pagination
          currentPage={currentPage}
          siblingCount={1}
          onPageChange={setCurrentPage}
          totalCount={data?.meta?.totalPages || 1}
          pageSize={PAGE_SIZE}
        />

        {/* Points config modal */}
        <CModal
          alignment="center"
          visible={showConfigModal}
          onClose={() => setShowConfigModal(false)}
        >
          <CModalBody>
            <h5 className="fs-5 mb-4 border-bottom pb-1">
              Configurer les points
            </h5>

            <div className="mb-3">
              <label htmlFor="config-type">Type</label>
              <select
                id="config-type"
                className="form-select mt-1"
                value={configType}
                onChange={(e) => setConfigType(e.target.value)}
              >
                <option value="">Sélectionner un type</option>
                <option value="RIDE">Courses</option>
              </select>
            </div>

            <div className="mb-3">
              <label htmlFor="config-points">Points de base</label>
              <input
                id="config-points"
                type="number"
                className="custom-input w-100 mt-1"
                value={points}
                onChange={(e) => setPoints(e.target.value)}
                placeholder="Points de base"
              />
            </div>

            <button
              type="button"
              disabled={updatePointsMutation.isLoading || points === ""}
              onClick={() =>
                updatePointsMutation.mutate({
                  points: Number(points),
                  type: configType,
                })
              }
              className="d-flex align-items-center justify-content-center btn btn-danger shadow-primary w-100 mt-2 p-2 fw-bold text-white"
            >
              {updatePointsMutation.isLoading ? (
                <>
                  <CSpinner size="sm" /> {general.fr.wait}
                </>
              ) : (
                "Sauvegarder"
              )}
            </button>
          </CModalBody>
        </CModal>
      </section>
    </AdminRoute>
  );
}
